import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@design-system/Card';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
} from '@design-system/Dialog';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useSkipOrder,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';

import {
  formatSMPDate,
  getNewNextOrderDate,
  formatDateToSMPDate,
} from './utils/smpHelpers';
import { useUIMessages } from './hooks/useUIMessages';
import { SMPDialogContent, SMPMutationErrorDialog, OrderInProgressDisclaimer, ShimmerImage } from './components';
import { SMP_PATH } from '@/constants/paths';

const SkipConfirmation = ({
  nextOrderDate,
  newOrderDate,
  planName,
  productImage,
  isSubmitting,
  onSubmit,
  onCancel,
}: {
  nextOrderDate: string;
  newOrderDate: string;
  planName?: string;
  productImage?: string;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) => (
  <>
    <p className="typography-h3 text-primary max-w-3xl pb-5">
      Are you sure you want to skip your next order?
    </p>

    <Card data-testid="smp-skipOrder-confirmCard">
      <CardContent className="flex flex-col gap-6 py-6">
        {planName && (
          <div className="flex items-center gap-5">
            {productImage && (
              <ShimmerImage src={productImage} alt={planName} containerClassName="size-[88px] rounded-xl overflow-hidden bg-background shrink-0" className="size-full object-cover" />
            )}
            <span className="typography-body text-primary">{planName}</span>
          </div>
        )}

        <div className="h-px w-full bg-stroke-secondary-subtle" />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="typography-caption1 text-tertiary uppercase">
              Previous Order Date
            </span>
            <span className="typography-body text-primary">{nextOrderDate}</span>
          </div>

          <div className="flex flex-col gap-1">
            <span className="typography-caption1 text-tertiary uppercase">
              New Order Date
            </span>
            <span className="typography-body text-[#009E82]">{newOrderDate}</span>
          </div>
        </div>
      </CardContent>
    </Card>

    <div className="flex flex-col gap-4 w-full max-w-sm pt-5">
      <Button
        onClick={onSubmit}
        disabled={isSubmitting}
        data-testid="smp-skipOrder-submit"
      >
        {isSubmitting ? 'Skipping...' : 'Yes, skip my next order'}
      </Button>
      <Button
        variant="secondary"
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid="smp-skipOrder-cancel"
      >
        Cancel
      </Button>
    </div>
  </>
);

const SuccessDialog = ({
  open,
  newOrderDate,
  successHtml,
  onClose,
}: {
  open: boolean;
  newOrderDate: string;
  successHtml?: string;
  onClose: () => void;
}) => (
  <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
    <SMPDialogContent>
      <DialogHeader className="gap-6 text-start">
        {successHtml ? (
          <DialogDescription asChild>
            <div
              className="typography-body text-primary [&_p]:mb-3"
              dangerouslySetInnerHTML={{ __html: successHtml }}
            />
          </DialogDescription>
        ) : (
          <DialogDescription className="typography-body text-primary">
            Your next order date successfully changed to {newOrderDate}.
          </DialogDescription>
        )}
      </DialogHeader>
    </SMPDialogContent>
  </Dialog>
);

// ============================================
// Main Page
// ============================================

type PageStep = 'order-in-progress' | 'confirmation';

export const SMPSkipOrderPage = () => {
  const navigate = useNavigate();
  const {
    subscriptionId,
    subscriptionDetail,
    hasOrderInProgress,
    uiMessages,
    showMaintenance,
    isLoading: isSMPLoading,
    isError: isSMPError,
  } = useSMPData({
    skip: { discounts: true, changePlan: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const { getMessageText } = useUIMessages(uiMessages);

  const skipMutation = useSkipOrder();

  const [step, setStep] = useState<PageStep | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [submittedNewOrderDate, setSubmittedNewOrderDate] = useState<string | null>(null);

  const goBack = () => navigate(SMP_PATH);

  const lacoreOrder = subscriptionDetail?.lacore_order;

  useEffect(() => {
    if (isSMPLoading || initialized || !subscriptionDetail) return;
    setInitialized(true);
    setStep(hasOrderInProgress ? 'order-in-progress' : 'confirmation');
  }, [isSMPLoading, initialized, subscriptionDetail, hasOrderInProgress]);

  if (isSMPLoading) {
    return (
      <section className="page-container flex flex-col gap-12 pb-24">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </section>
    );
  }

  if (isSMPError || !subscriptionDetail) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  const firstLineItem = subscriptionDetail.line_items?.[0];
  const planName = firstLineItem?.product_name || firstLineItem?.title;
  const productImage = firstLineItem?.image;

  const nextOrderDatetime = subscriptionDetail.next_order_datetime;
  const nextOrderDateFormatted = formatSMPDate(nextOrderDatetime);
  const newOrderDate = getNewNextOrderDate(nextOrderDatetime);
  const newOrderDateFormatted = formatDateToSMPDate(newOrderDate);

  const handleSkipSubmit = () => {
    setSubmittedNewOrderDate(newOrderDateFormatted);
    skipMutation.mutate(
      {
        subscriptionId,
        newDate: newOrderDate,
        date: new Date(nextOrderDatetime),
      },
      {
        onSuccess: () => {
          setShowSuccessDialog(true);
        },
        onError: () => {
          setSubmittedNewOrderDate(null);
          setShowErrorDialog(true);
        },
      },
    );
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    setSubmittedNewOrderDate(null);
    goBack();
  };

  const pageTitle =
    step === 'order-in-progress' ? 'Order in Progress' : 'Skip Next Order';

  return (
    <section
      className="page-container flex flex-col gap-6 pb-24"
      data-testid="smp-skipOrderPage"
    >
      <div className="flex flex-col gap-6">
        <NavBar
          returnButtonTitle="Back"
          className="md:-ml-9"
          onReturnClick={goBack}
        />

        <h1
          className="typography-h6 text-primary pt-3 pt-1"
          data-testid="smp-skipOrder-title"
        >
          {pageTitle}
        </h1>
      </div>

      {step === 'order-in-progress' && lacoreOrder && (
        <OrderInProgressDisclaimer
          lacoreOrder={lacoreOrder}
          planName={planName}
          productImage={productImage}
          onContinue={() => setStep('confirmation')}
          onCancel={goBack}
          continueLabel="Proceed to Skip"
        />
      )}

      {step === 'confirmation' && (
        <SkipConfirmation
          nextOrderDate={nextOrderDateFormatted}
          newOrderDate={newOrderDateFormatted}
          planName={planName}
          productImage={productImage}
          isSubmitting={skipMutation.isPending}
          onSubmit={handleSkipSubmit}
          onCancel={goBack}
        />
      )}

      <SuccessDialog
        open={showSuccessDialog}
        newOrderDate={submittedNewOrderDate ?? newOrderDateFormatted}
        successHtml={
          getMessageText('smp_new_order_date_changed', 'bodyHtml')
            ?.replace('new_date', submittedNewOrderDate ?? newOrderDateFormatted) || undefined
        }
        onClose={handleSuccessClose}
      />

      <SMPMutationErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        description="Something went wrong while skipping your order. Please try again."
      />
    </section>
  );
};
